import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Check,
  Edit3,
  KeyRound,
  ListPlus,
  MailCheck,
  Plus,
  RefreshCcw,
  Server,
  ServerCog,
  ShieldCheck,
  Trash2,
  Wifi
} from "lucide-react";
import type { CustomEmailProvider, EmailAccount, User } from "../types";
import { api } from "../lib/api";
import { blankAccount, providerPreset, type AccountForm } from "../lib/accountPresets";
import { formatDateTime } from "../lib/format";
import BulkAccountsModal from "../components/BulkAccountsModal";
import ProviderManagerModal from "../components/ProviderManagerModal";
import RichTextEditor from "../components/RichTextEditor";
import { Badge, Button, EmptyState, IconButton, Loading, Modal, Switch } from "../components/ui";
export default function AccountsPage({
  user,
  notify,
  onChanged
}: {
  user: User;
  notify: (tone: "success" | "error" | "info", title: string, message?: string) => void;
  onChanged: () => void;
}) {
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [customProviders, setCustomProviders] = useState<CustomEmailProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EmailAccount | null | "new">(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [providersOpen, setProvidersOpen] = useState(false);
  const [form, setForm] = useState<AccountForm>(blankAccount);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string>();
  const [syncing, setSyncing] = useState<string>();

  const load = async () => {
    setLoading(true);
    try {
      const [accountResults, providerResults] = await Promise.all([
        api.accounts(),
        user.isAdministrator ? api.customProviders() : Promise.resolve([])
      ]);
      setAccounts(accountResults);
      setCustomProviders(providerResults);
    } catch (error) {
      notify("error", "Couldn’t load accounts", (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEditor = (account?: EmailAccount) => {
    if (account) {
      setEditing(account);
      setForm({
        displayName: account.displayName,
        emailAddress: account.emailAddress,
        providerType: account.providerType,
        authType: account.authType,
        incomingProtocol: account.incomingProtocol,
        incomingServer: account.incomingServer ?? "",
        incomingPort: account.incomingPort ?? 993,
        incomingUseSsl: account.incomingUseSsl,
        outgoingServer: account.outgoingServer ?? "",
        outgoingPort: account.outgoingPort ?? 465,
        outgoingUseSsl: account.outgoingUseSsl,
        username: account.username ?? "",
        secret: "",
        signatureHtml: account.signatureHtml,
        isActive: account.isActive
      });
    } else {
      setEditing("new");
      setForm(blankAccount);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.createAccount(form);
        notify("success", "Email account added", "Test the connection before assigning it to your team.");
      } else if (editing) {
        await api.updateAccount(editing.id, form);
        notify(
          "success",
          "Account settings updated",
          form.secret.trim()
            ? "The replacement credential was encrypted with the current server keys. Test the connection now."
            : "Stored credentials were retained unless replaced."
        );
      }
      setEditing(null);
      await load();
      onChanged();
    } catch (error) {
      notify("error", "Couldn’t save account", (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const test = async (account: EmailAccount) => {
    setTesting(account.id);
    try {
      const result = await api.testAccount(account.id);
      notify(
        result.success ? "success" : "error",
        result.success ? "Connection successful" : "Connection needs attention",
        result.message
      );
      await load();
      if (result.success) onChanged();
    } catch (error) {
      notify("error", "Connection needs attention", (error as Error).message);
      await load();
    } finally {
      setTesting(undefined);
    }
  };

  const connectOAuth = async (provider: "Google" | "Yahoo") => {
    try {
      const result = await api.startOAuth(provider);
      window.open(result.authorizationUrl, "_blank", "noopener,noreferrer");
      notify("info", `${provider} authorization opened`, "Complete authorization in your browser, then return here and refresh accounts.");
    } catch (error) {
      notify("error", `${provider} OAuth unavailable`, (error as Error).message);
    }
  };

  const sync = async (account: EmailAccount) => {
    setSyncing(account.id);
    try {
      const result = await api.syncAccount(account.id);
      notify("success", "Mailbox synchronized", result.message);
      await load();
      onChanged();
    } catch (error) {
      notify("error", "Synchronization failed", (error as Error).message);
    } finally {
      setSyncing(undefined);
    }
  };

  const disable = async (account: EmailAccount) => {
    if (!window.confirm(`Disable ${account.displayName}? Its message history will be retained.`)) return;
    try {
      await api.disableAccount(account.id);
      notify("success", "Account disabled", "Historical messages remain searchable.");
      await load();
      onChanged();
    } catch (error) {
      notify("error", "Couldn’t disable account", (error as Error).message);
    }
  };

  return (
    <div className="page accounts-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Server-side connections</span>
          <h1>Email accounts</h1>
          <p>Connect and correct mailbox settings without exposing credentials to employees.</p>
        </div>
        {user.isAdministrator && (
          <div className="footer-actions account-heading-actions">
            <Button tone="secondary" onClick={() => connectOAuth("Google")}>Connect Google</Button>
            <Button tone="secondary" onClick={() => connectOAuth("Yahoo")}>Connect Yahoo</Button>
            <Button tone="secondary" icon={<ServerCog size={16} />} onClick={() => setProvidersOpen(true)}>
              Custom providers
            </Button>
            <Button tone="secondary" icon={<ListPlus size={16} />} onClick={() => setBulkOpen(true)}>
              Add in bulk
            </Button>
            <Button icon={<Plus size={17} />} onClick={() => openEditor()}>Add account</Button>
          </div>
        )}
      </div>

      <div className="security-banner">
        <span>
          <ShieldCheck size={21} />
        </span>
        <div>
          <strong>Protected credential boundary</strong>
          <p>
            Provider passwords and tokens are encrypted by the ASP.NET server and are never included
            in account responses, local storage, or frontend logs.
          </p>
        </div>
      </div>

      {loading ? (
        <Loading label="Checking account status…" />
      ) : accounts.length === 0 ? (
        <EmptyState
          icon={<MailCheck />}
          title="No email accounts yet"
          message="Connect your first mailbox to start managing conversations."
          action={
            user.isAdministrator ? (
              <Button icon={<Plus size={16} />} onClick={() => openEditor()}>
                Add account
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="account-grid">
          {accounts.map((account) => (
            <article className={`account-card ${!account.isActive ? "account-disabled" : ""}`} key={account.id}>
              <header>
                <span className="account-provider-icon">
                  <MailCheck size={20} />
                </span>
                <div>
                  <h2>{account.displayName}</h2>
                  <p>{account.emailAddress}</p>
                </div>
                <Badge
                  tone={
                    account.connectionStatus === "Connected"
                      ? "success"
                      : account.connectionStatus === "Needs attention"
                        ? "warning"
                        : "neutral"
                  }
                >
                  {account.connectionStatus === "Connected" && <Check size={12} />}
                  {account.connectionStatus}
                </Badge>
              </header>
              <dl>
                <div>
                  <dt>Provider</dt>
                  <dd>{account.providerType}</dd>
                </div>
                <div>
                  <dt>Incoming</dt>
                  <dd>
                    {account.providerType === "Demo"
                      ? "Local demo"
                      : `${account.incomingProtocol} · ${account.incomingPort}`}
                  </dd>
                </div>
                <div>
                  <dt>Authentication</dt>
                  <dd>
                    {account.authType}
                    {account.hasStoredSecret && <KeyRound size={13} />}
                  </dd>
                </div>
                <div>
                  <dt>Last checked</dt>
                  <dd>{account.lastSyncAt ? formatDateTime(account.lastSyncAt) : "Not tested"}</dd>
                </div>
              </dl>
              <footer>
                {user.isAdministrator && (
                  <>
                    <Button
                      tone="secondary"
                      icon={
                        testing === account.id ? (
                          <RefreshCcw size={15} className="spin" />
                        ) : (
                          <Wifi size={15} />
                        )
                      }
                      disabled={testing === account.id || !account.isActive}
                      onClick={() => test(account)}
                    >
                      Test connection
                    </Button>
                    {account.providerType !== "Demo" && (
                      <Button
                        tone="ghost"
                        icon={<RefreshCcw size={15} className={syncing === account.id ? "spin" : ""} />}
                        disabled={syncing === account.id || !account.isActive}
                        onClick={() => sync(account)}
                      >
                        Sync now
                      </Button>
                    )}
                    <IconButton label="Edit account" onClick={() => openEditor(account)}>
                      <Edit3 size={16} />
                    </IconButton>
                    <IconButton label="Disable account" onClick={() => disable(account)}>
                      <Trash2 size={16} />
                    </IconButton>
                  </>
                )}
              </footer>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? "Add email account" : "Edit email account"}
        subtitle={
          editing === "new"
            ? "Credentials are encrypted as soon as they reach the server."
            : "Leave the secret blank to keep the current stored credential."
        }
        wide
      >
        <div className="account-editor">
          <section className="form-section">
            <div className="form-section-heading">
              <span>1</span>
              <div>
                <h3>Account identity</h3>
                <p>How this mailbox appears throughout the workspace.</p>
              </div>
            </div>
            <div className="form-grid two">
              <label className="field">
                <span>Display name</span>
                <input
                  value={form.displayName}
                  onChange={(event) => setForm({ ...form, displayName: event.target.value })}
                  placeholder="Reservations"
                />
              </label>
              <label className="field">
                <span>Email address</span>
                <input
                  type="email"
                  value={form.emailAddress}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      emailAddress: event.target.value,
                      username: form.username || event.target.value
                    })
                  }
                  placeholder="reservations@company.com"
                />
              </label>
            </div>
            <div className="provider-options">
              {[
                { provider: "Custom", Icon: Server, subtitle: "IMAP / SMTP" },
                { provider: "Gmail", Icon: MailCheck, subtitle: "Google mail" },
                { provider: "Yahoo", Icon: MailCheck, subtitle: "Yahoo mail" },
                ...customProviders.map((provider) => ({
                  provider: provider.name,
                  Icon: ServerCog,
                  subtitle: `${provider.incomingProtocol} preset`
                })),
                { provider: "Demo", Icon: ShieldCheck, subtitle: "No external delivery" }
              ].filter(({ provider }) => import.meta.env.DEV || provider !== "Demo").map(({ provider, Icon, subtitle }) => (
                <button
                  type="button"
                  key={provider}
                  className={form.providerType === provider ? "active" : ""}
                  onClick={() => setForm(providerPreset(provider, form, customProviders))}
                >
                  <Icon size={18} />
                  <span>
                    <strong>{provider}</strong>
                    <small>{subtitle}</small>
                  </span>
                </button>
              ))}
            </div>
          </section>

          {form.providerType !== "Demo" && (
            <>
              <section className="form-section">
                <div className="form-section-heading">
                  <span>2</span>
                  <div>
                    <h3>Mail servers</h3>
                    <p>IMAP is preferred so mail stays synchronized.</p>
                  </div>
                </div>
                <div className="form-grid server-grid">
                  <label className="field">
                    <span>Incoming protocol</span>
                    <select
                      value={form.incomingProtocol}
                      onChange={(event) =>
                        setForm({ ...form, incomingProtocol: event.target.value })
                      }
                    >
                      <option>IMAP</option>
                      <option>POP</option>
                    </select>
                  </label>
                  <label className="field server-host">
                    <span>Incoming server</span>
                    <input
                      value={form.incomingServer}
                      onChange={(event) =>
                        setForm({ ...form, incomingServer: event.target.value })
                      }
                      placeholder="imap.company.com"
                    />
                  </label>
                  <label className="field">
                    <span>Port</span>
                    <input
                      type="number"
                      value={form.incomingPort}
                      onChange={(event) =>
                        setForm({ ...form, incomingPort: Number(event.target.value) })
                      }
                    />
                  </label>
                  <label className="field server-host">
                    <span>Outgoing SMTP server</span>
                    <input
                      value={form.outgoingServer}
                      onChange={(event) =>
                        setForm({ ...form, outgoingServer: event.target.value })
                      }
                      placeholder="smtp.company.com"
                    />
                  </label>
                  <label className="field">
                    <span>Port</span>
                    <input
                      type="number"
                      value={form.outgoingPort}
                      onChange={(event) =>
                        setForm({ ...form, outgoingPort: Number(event.target.value) })
                      }
                    />
                  </label>
                </div>
                <div className="inline-switches">
                  <label>
                    <Switch
                      checked={form.incomingUseSsl}
                      onChange={(incomingUseSsl) => setForm({ ...form, incomingUseSsl })}
                      label="Incoming SSL"
                    />
                    Incoming SSL/TLS
                  </label>
                  <label>
                    <Switch
                      checked={form.outgoingUseSsl}
                      onChange={(outgoingUseSsl) => setForm({ ...form, outgoingUseSsl })}
                      label="Outgoing SSL"
                    />
                    Outgoing SSL/TLS
                  </label>
                </div>
              </section>

              <section className="form-section">
                <div className="form-section-heading">
                  <span>3</span>
                  <div>
                    <h3>Server authentication</h3>
                    <p>Only the backend can decrypt and use these credentials.</p>
                  </div>
                </div>
                <div className="form-grid two">
                  <label className="field">
                    <span>Username</span>
                    <input
                      value={form.username}
                      onChange={(event) => setForm({ ...form, username: event.target.value })}
                      autoComplete="off"
                    />
                  </label>
                  <label className="field">
                    <span>{editing === "new" ? "Password / app password" : "Replace stored secret"}</span>
                    <input
                      type="password"
                      value={form.secret}
                      onChange={(event) => setForm({ ...form, secret: event.target.value })}
                      autoComplete="new-password"
                      placeholder={editing === "new" ? "Required" : "Leave blank to keep current"}
                    />
                  </label>
                </div>
                {(form.providerType === "Gmail" || form.providerType === "Yahoo") && (
                  <div className="oauth-roadmap-note">
                    <AlertTriangle size={17} />
                    <span>
                      {form.providerType === "Yahoo"
                        ? "Use a generated Yahoo third-party app password here, not your regular Yahoo password. Spaces in the generated code are accepted."
                        : "Prefer the Connect Gmail button for OAuth. Enter an app password here only when you intentionally choose password-based provider access."}
                    </span>
                  </div>
                )}
              </section>
            </>
          )}
          <div className="switch-row">
            <div>
              <strong>Account active</strong>
              <span>Inactive accounts cannot sync or send messages.</span>
            </div>
            <Switch
              checked={form.isActive}
              onChange={(isActive) => setForm({ ...form, isActive })}
              label="Account active"
            />
          </div>
          <section className="form-section">
            <div className="form-section-heading">
              <span>4</span>
              <div>
                <h3>Email signature</h3>
                <p>Automatically added when this account is selected in the composer.</p>
              </div>
            </div>
            <RichTextEditor
              value={form.signatureHtml}
              onChange={(signatureHtml) => setForm({ ...form, signatureHtml })}
              minHeight={140}
            />
          </section>
        </div>
        <div className="modal-footer">
          <Button tone="secondary" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving securely…" : editing === "new" ? "Add account" : "Save settings"}
          </Button>
        </div>
      </Modal>

      <BulkAccountsModal
        open={bulkOpen}
        providers={customProviders}
        onClose={() => setBulkOpen(false)}
        onCreated={async () => {
          await load();
          onChanged();
        }}
        notify={notify}
      />
      <ProviderManagerModal
        open={providersOpen}
        providers={customProviders}
        onClose={() => setProvidersOpen(false)}
        onChanged={load}
        notify={notify}
      />
    </div>
  );
}

